import { TranslateService } from "@ngx-translate/core";
import { XMLParser } from "fast-xml-parser";

import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { ENV } from "../../app.runtime";
import { NewsRepositoryPort } from "../domain/ports/news-repository.port";
import { NewsItem } from "../domain/schemas/news-item";
import { RssNewsItem } from "./schemas/rss-news-item";
import { RssNewsList } from "./schemas/rss-news-list";

@Injectable({
  providedIn: "root",
})
export class RssNewsRepository implements NewsRepositoryPort {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);

  private parser = new XMLParser();

  async getNewsItemsForLanguage(language: string): Promise<NewsItem[]> {
    const feed = await this.getRssFeedForLanguage(language);
    const itemsRaw = feed?.rss?.channel?.item ?? [];
    const items = !Array.isArray(itemsRaw) ? [itemsRaw] : itemsRaw;
    return items.map((i) => toNewsItem(i));
  }

  async getRssFeedForLanguage(language: string): Promise<RssNewsList> {
    const headers = new HttpHeaders({
      "Cache-Control": "no-cache, no-store, must-revalidate, post-check=0, pre-check=0",
    });

    const url = ENV.NEWS_RSS_FEED_URL ? ENV.NEWS_RSS_FEED_URL.replace("/feed/", `/${language}/feed/`) : "";
    const supported = this.translate.getLangs();
    if (!supported.includes(language)) {
      console.warn(`Attempting to use an unexpected language ${language}; expected one of ${supported}.`);
    }
    const feedXml = await this.http.get(url, { headers, responseType: "text" }).toPromise();
    if (!feedXml) throw new Error("Fetching news XML failed");
    return this.parser.parse(feedXml);
  }
}

export const toNewsItem = (rss: RssNewsItem): NewsItem => {
  return {
    author: rss["dc:creator"],
    categories: Array.isArray(rss.category) ? rss.category : [rss.category],
    contents: rss["content:encoded"],
    description: rss.description,
    newsItemId: parseGuid(rss.guid),
    publishedAt: parseDate(rss.pubDate),
    title: rss.title,
  };
};

export const parseDate = (value: string | undefined): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

export const parseGuid = (value: string) => {
  const parts = value.split("=");
  const last = parts.length - 1;

  const isDigit = (s: string) => !isNaN(Number(s));
  if (!isDigit(parts[last])) {
    console.warn(`Not able to extract guid from ${value} at position ${last}; returning empty string`);
    return "";
  }

  return parts[last];
};
